import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { FaInstagram, FaFacebookF, FaSpotify, FaSoundcloud } from 'react-icons/fa';
import BandlabIcon from '../assets/bandlab-icon.png'
import XIcon from '../assets/x-icon.png'
import { IoShareOutline } from 'react-icons/io5';
import { AudioContext } from '../context/AudioContext';
import TrackItem from './TrackItem';
import '../styles/OtherUserDiscography.css';

const OtherUserDiscography = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { handleTrackClick } = useContext(AudioContext);
  const [uploads, setUploads] = useState([]);
  const topTracks = uploads
    .slice()
    .sort((a, b) => (b.plays || 0) - (a.plays || 0))
    .slice(0, 6);
  const recentTracks = uploads
    .slice()
    .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))
    .slice(0, 6);
  const [pinnedTracks, setPinnedTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [userData, setUserData] = useState({
    artistName: '',
    profilePicture: '',
    bio: '',
    socialLinks: {},
  });
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  useEffect(() => {
    const fetchDiscographyForOtherUser = async (firebaseUid) => {
      try {
        const userResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/users/firebase/${firebaseUid}`
        );
        const data = userResponse.data;
        if (!data.socialLinks) {
          data.socialLinks = {};
        }
        setUserData(data);

        const pinnedResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/pinned`,
          { params: { firebaseUid } }
        );
        setPinnedTracks(pinnedResponse.data);

        const uploadsResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads`,
          { params: { firebaseUid } }
        );
        setUploads(uploadsResponse.data);

        const albumResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/album?firebaseUid=${firebaseUid}`
        );
        setAlbums(albumResponse.data);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching other artist data:', error.response ? error.response.data : error.message);
        setLoading(false);
      }
    };

    if (id) {
      fetchDiscographyForOtherUser(id);
    }
  }, [id]);

  const handleShareDiscography = async () => {
    if (!id) {
      window.alert('No user ID available to share this discography.');
      return;
    }

    const deepLink = `https://syncmusic.app/otheruserdiscography/${id}`;
    const message = `Check out ${userData.artistName}'s discography on Sync: ${deepLink}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `Share ${userData.artistName}'s Discography`,
          text: message,
          url: deepLink,
        });
      } else {
        await navigator.clipboard.writeText(message);
        window.alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing discography:', error);
      window.alert('Failed to share discography.');
    }
  };

  const handleLikeTrack = async (trackId, trackUserId) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      window.alert('You must be logged in to like a track.');
      return;
    }

    if (trackUserId === currentUser.uid) {
      window.alert("You can't like your own track.");
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        'https://sync-api-troz.onrender.com/api/uploads/like',
        { trackId, firebaseUid: currentUser.uid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Track liked successfully!');
      setUploads((prev) =>
        prev.map((track) =>
          track._id === trackId
            ? { ...track, likes: (track.likes || 0) + 1, likedBy: [...(track.likedBy || []), currentUser.uid] }
            : track
        )
      );
      setPinnedTracks((prev) =>
        prev.map((track) =>
          track._id === trackId
            ? { ...track, likes: (track.likes || 0) + 1, likedBy: [...(track.likedBy || []), currentUser.uid] }
            : track
        )
      );
    } catch (error) {
      console.error('Error liking track:', error.response?.data || error.message);
      window.alert(
        error.response?.data?.message === 'You have already liked this track'
          ? 'You have already liked this track.'
          : 'An error occurred while liking the track.'
      );
    }
  };

  const handleStarTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    if (!firebaseUid) {
      window.alert('You must be logged in to star a track.');
      return;
    }

    const track = uploads.find((t) => t._id === trackId);
    if (track?.user?.firebaseUid === firebaseUid) {
      window.alert("You can't star your own track.");
      return;
    }

    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/star', {
        trackId,
        userId: firebaseUid,
      });
      window.alert('Track starred successfully!');
      setUploads((prev) =>
        prev.map((track) =>
          track._id === trackId
            ? {
                ...track,
                stars: (track.stars || 0) + 1,
                starredBy: [...(track.starredBy || []), firebaseUid],
              }
            : track
        )
      );
      setPinnedTracks((prev) =>
        prev.map((track) =>
          track._id === trackId
            ? {
                ...track,
                stars: (track.stars || 0) + 1,
                starredBy: [...(track.starredBy || []), firebaseUid],
              }
            : track
        )
      );
    } catch (error) {
      console.error('Error starring track:', error.response?.data || error.message);
      window.alert(
        error.response?.data?.message === 'You have already starred a track from this artist'
          ? 'You already starred a track from this user.'
          : 'An error occurred while starring the track.'
      );
    }
  };

  const openLink = (url) => {
    if (url) {
      window.open(url, '_blank');
    } else {
      window.alert('Invalid link.');
    }
  };

  if (loading) {
    return (
      <div className="other-user-discography-loading">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="other-user-discography-page">
      <div className="other-user-discography-container">
        <div className="other-user-banner">
          {userData.profilePicture ? (
            <img
              src={userData.profilePicture}
              alt="Profile"
              className="other-user-banner-image"
            />
          ) : (
            <div className="other-user-banner-placeholder" />
          )}
          <p className="other-user-artist-name">{userData.artistName}</p>
        </div>

        <div className="other-user-social-links">
          {userData.socialLinks.twitter && (
            <button
              onClick={() => openLink(userData.socialLinks.twitter)}
              className="other-user-social-icon"
            >
              <img
                src={XIcon}
                alt="X"
                className="other-user-social-icon-img"
              />
            </button>
          )}
          {userData.socialLinks.instagram && (
            <button
              onClick={() => openLink(userData.socialLinks.instagram)}
              className="other-user-social-icon"
            >
              <FaInstagram size={24} color="#C13584" />
            </button>
          )}
          {userData.socialLinks.facebook && (
            <button
              onClick={() => openLink(userData.socialLinks.facebook)}
              className="other-user-social-icon"
            >
              <FaFacebookF size={24} color="#4267B2" />
            </button>
          )}
          {userData.socialLinks.soundcloud && (
            <button
              onClick={() => openLink(userData.socialLinks.soundcloud)}
              className="other-user-social-icon"
            >
              <FaSoundcloud size={24} color="#FF5500" />
            </button>
          )}
          {userData.socialLinks.spotify && (
            <button
              onClick={() => openLink(userData.socialLinks.spotify)}
              className="other-user-social-icon"
            >
              <FaSpotify size={24} color="#1DB954" />
            </button>
          )}
          {userData.socialLinks.bandlab && (
            <button
              onClick={() => openLink(userData.socialLinks.bandlab)}
              className="other-user-social-icon"
            >
              <img
                src={BandlabIcon}
                alt="BandLab"
                className="other-user-social-icon-img"
              />
            </button>
          )}
        </div>

        <div className="other-user-bio">
          <p className="other-user-bio-label">Artist Biography:</p>
          <p className="other-user-bio-text">{userData.bio || 'No bio available.'}</p>
        </div>

        <div className="other-user-header">
          <p className="other-user-discography-header">{userData.artistName}</p>
          <button onClick={handleShareDiscography} className="other-user-share-button">
            <IoShareOutline size={24} />
          </button>
        </div>

        <div className="other-user-results">
          {pinnedTracks.length > 0 && (
            <div className="other-user-section">
              <div className="other-user-section-header-container">
                <p className="other-user-section-header">Pinned Tracks</p>
              </div>
              {pinnedTracks.map((track) => (
                <TrackItem
                  key={track._id}
                  track={track}
                  onPlay={() => handleTrackClick(track)}
                  onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
                  onStar={() => handleStarTrack(track._id)}
                  currentUserId={auth.currentUser?.uid}
                  isOwner={false}
                  showPinButton={false}
                  showDeleteButton={false}
                />
              ))}
            </div>
          )}

          {topTracks.length > 0 && (
            <div className="other-user-section">
              <div className="other-user-section-header-container">
                <p className="other-user-section-header">Top Tracks</p>
                <button
                  onClick={() => navigate(`/otheruserdiscography/${id}/top-tracks`)}
                  className="other-user-see-all-text"
                >
                  See All
                </button>
              </div>
              {topTracks.map((track) => (
                <TrackItem
                  key={track._id}
                  track={track}
                  onPlay={() => handleTrackClick(track)}
                  onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
                  onStar={() => handleStarTrack(track._id)}
                  currentUserId={auth.currentUser?.uid}
                  isOwner={false}
                  showPinButton={false}
                  showDeleteButton={false}
                />
              ))}
            </div>
          )}

          {recentTracks.length > 0 && (
            <div className="other-user-section">
              <div className="other-user-section-header-container">
                <p className="other-user-section-header">All Tracks</p>
                <button
                  onClick={() => navigate(`/otheruserdiscography/${id}/all-tracks`)}
                  className="other-user-see-all-text"
                >
                  See All
                </button>
              </div>
              {recentTracks.map((track) => (
                <TrackItem
                  key={track._id}
                  track={track}
                  onPlay={() => handleTrackClick(track)}
                  onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
                  onStar={() => handleStarTrack(track._id)}
                  currentUserId={auth.currentUser?.uid}
                  isOwner={false}
                  showPinButton={false}
                  showDeleteButton={false}
                />
              ))}
            </div>
          )}

          {albums.length > 0 && (
            <div className="other-user-section">
              <div className="other-user-section-header-container">
                <p className="other-user-section-header">Albums</p>
              </div>
              {albums.map((album) => (
                <div
                  key={album._id}
                  onClick={() => navigate(`/otheruserdiscography/${id}/album/${album._id}`)}
                  className="other-user-album-item"
                >
                  {album.coverPhoto ? (
                    <img
                      src={album.coverPhoto}
                      alt="Album Cover"
                      className="other-user-album-cover"
                    />
                  ) : (
                    <div className="other-user-album-cover-placeholder" />
                  )}
                  <p className="other-user-album-title">{album.albumTitle}</p>
                </div>
              ))}
            </div>
          )}

          {uploads.length === 0 && (
            <p className="other-user-no-results">This artist hasn't uploaded any tracks yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtherUserDiscography;